import {
  Alert,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  QuestionAnswer,
  ExpandMore,
  ExpandLess,
  PlayArrow,
  Lock,
  PlayCircleFilled,
  Bookmark,
  BookmarkBorder
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import DaySelector from "../Extra/DayChanger";
import { ReactComponent as LockIcon } from "../../assets/lock1.svg";
import {
  getCurrentUserAllTrainings,
  getCurrentUserLevels,
  getCurrentUserTrainings,
  updateTrainingById,
  updateTrainingProgressById
} from "../../features/training/trainingSlice";
import {
  selectAllTrainingsData,
  selectAllTrainingsWithTitleData,
  selectLevelData
} from "../../features/training/trainingSelectors";
import CircularProgressStatic from "../Extra/ProgressTracker";
import RenderHtml from "../Extra/RenderHtml/RenderHtml";
import dayjs from "dayjs";
import { selectCurrentUser } from "../../features/auth/authSelectors";
import useThemeColor from "../../utils/useTheme";
import {
  purchaseTraining,
  updateTrainingProgress
} from "../../api/trainingApi";
import useIsMobile from "../Extra/useMobile";
import utc from "dayjs/plugin/utc";
import SubscriptionModal from "../Modals/ProgramSubscriptionModal";
import {
  allowedCountries,
  countryRates
} from "../../utils/countryRatesMapping";
dayjs.extend(utc);

const Programs = ({
  open,
  activeLevelForMobile,
  setOpenSubscriptionModal,
  setActiveLevelForMobile
}) => {
  const [selectedLevel, setSelectedLevel] = React.useState("");
  const dispatch = useDispatch();
  const styles = useThemeColor();
  const allLevels = useSelector(selectLevelData);
  const theme = useTheme();
  const isInMobile = useIsMobile();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")) || isInMobile;
  const trainingData = useSelector(selectAllTrainingsData);
  const trainingWithTitleData = useSelector(selectAllTrainingsWithTitleData);
  const [activeDay, setActiveDay] = useState(0);
  const [tocDay, setTocDay] = useState(0);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const userData = useSelector(selectCurrentUser);
  const [isAudioFinished, setIsAudioFinished] = useState(false);
  const [expandedLevels, setExpandedLevels] = useState({});
  const [tocOpen, setTocOpen] = useState(false);
  // Add this function to handle expand/collapse
  const toggleLevelExpansion = (levelName) => {
    setExpandedLevels((prev) => ({
      ...prev,
      [levelName]: !prev[levelName]
    }));
  };
  const handleLevelSelect = (level) => {
    setSelectedLevel(level);
    setActiveLevelForMobile(true);
  };
  const currentLevel = useMemo(() => {
    return allLevels?.find((item) => item?.levelName === selectedLevel);
  }, [allLevels, selectedLevel]);
  console.log("currentLevel", currentLevel);
  useEffect(() => {
    const existingData = JSON.parse(
      localStorage.getItem("lastProgramVisitedLevel") || "{}"
    );

    if (existingData?.level) {
      setSelectedLevel(existingData.level);
    }

    // Set activeDay from localStorage if it exists
    if (existingData?.activeDay) {
      setActiveDay(existingData.activeDay);
    }
  }, []);
  useEffect(() => {
    dispatch(getCurrentUserLevels(dayjs().format("YYYY-MM-DD")));
    dispatch(getCurrentUserAllTrainings(dayjs().format("YYYY-MM-DD")));
  }, [dispatch]);

  useEffect(() => {
    if (currentLevel?.levelId)
      dispatch(getCurrentUserTrainings(currentLevel?.levelId));
  }, [dispatch, currentLevel?.levelId]);

  React.useEffect(() => {
    window.scrollTo(0, 0);
    const scrollableElement = document.getElementById("dashboard-main");
    scrollableElement.scrollTo(0, 0);
    localStorage.setItem("lastVisitedRoute", "/programs");
  }, []);

  const calculateActiveDay = useCallback(() => {
    if (!trainingData || trainingData.length === 0) return 0;

    // Check if we have activeDay stored in localStorage first
    const existingSelectedTrainingDay = localStorage.getItem(
      "lastProgramVisitedLevel"
    );

    if (existingSelectedTrainingDay) {
      const storedData = JSON.parse(existingSelectedTrainingDay);
      const storedActiveDay = storedData?.activeDay;
      const storedLevel = storedData?.level;

      // Only use stored activeDay if it's for the current level and exists in trainingData
      if (storedActiveDay && storedLevel === selectedLevel) {
        const dayExists = trainingData.some(
          (item) => item.day === `Day ${storedActiveDay}`
        );
        if (dayExists) {
          return storedActiveDay;
        }
      }
    }

    // If no stored activeDay, calculate it normally
    let activeDay = 0;

    for (let i = 0; i < trainingData?.length; i++) {
      const task = trainingData[i];
      const day = +task.day.split(" ")[1];
      if (!task.completedOn && day) {
        activeDay += day;
        break;
      }
    }

    // Limit activeDay to the maximum day in enabledDays
    const maxEnabledDay = Math.max(
      ...(currentLevel?.trainingCompletionPercentage === 100 &&
      currentLevel?.totalTrainingDays !== 0
        ? Array.from(
            { length: currentLevel?.completedTrainingDays },
            (_, i) => i + 1
          )
        : !userData?.isSubscriptionNeeded
        ? Array.from(
            {
              length:
                dayjs()
                  .startOf("day")
                  .diff(
                    dayjs(
                      dayjs(userData?.[`${currentLevel?.levelName}_start_date`])
                        .utc()
                        .format("YYYY-MM-DD")
                    ).startOf("day"),
                    "day"
                  ) + 1
            },
            (_, i) => i + 1
          )
        : !userData?.isTrainingFreeTrialExpired
        ? Array.from(
            {
              length: Math.min(
                5,
                dayjs()
                  .startOf("day")
                  .diff(
                    dayjs(
                      dayjs(userData?.[`${currentLevel?.levelName}_start_date`])
                        .utc()
                        .format("YYYY-MM-DD")
                    ).startOf("day"),
                    "day"
                  ) + 1
              )
            },
            (_, i) => i + 1
          )
        : [])
    );

    return Math.min(activeDay, maxEnabledDay, trainingData.length) || 1;
  }, [trainingData, userData, currentLevel, selectedLevel]);

  const [startDay, setStartDay] = useState(1); // Start displaying from day 1
  const [openModal, setOpenModal] = useState(false);
  const days = Array.from({ length: trainingData?.length }, (_, i) => i + 1); // Days 1 to 30
  const visibleDaysCount = isMobile ? 5 : 8; // Number of days to show at a time
  const visibleDays = days.slice(startDay - 1, startDay - 1 + visibleDaysCount);
  const isTablet = useMediaQuery("(min-width: 600px) and (max-width: 900px)");
  const isLaptop = useMediaQuery("(min-width: 900px) and (max-width: 1300px)");

  useEffect(() => {
    if (trainingData) {
      const currentDay = tocDay ? tocDay : calculateActiveDay(trainingData);
      setActiveDay(currentDay);
      if (currentDay > visibleDaysCount) {
        const pageIndex = Math.floor((currentDay - 1) / visibleDaysCount);
        // Calculate startDay for that page
        const calculatedStartDay = pageIndex * visibleDaysCount + 1;
        setStartDay(calculatedStartDay);
      } else {
        setStartDay(1); // Reset to 1 if currentDay is less than or equal to visibleDaysCount
      }
    }
  }, [trainingData]);

  const handleDayClick = (day) => {
    setActiveDay(day);
    setIsAudioFinished(false);
    if (currentLevel?.trainingCompletionPercentage === 100) {
      localStorage.setItem(
        "lastProgramVisitedLevel",
        JSON.stringify({
          level: currentLevel?.levelName,
          activeDay: day
        })
      );
    }
  };

  const currentActiveData = useMemo(() => {
    return trainingData?.find((item) => item.day === `Day ${activeDay}`);
  }, [trainingData, activeDay]);

  const allCompletedData = useMemo(() => {
    return trainingData
      ?.filter((item) => item.completedOn)
      ?.map((item) => +item.day.split(" ")?.[1]);
  }, [trainingData]);

  const handleNext = () => {
    if (startDay + visibleDaysCount - 1 < days.length) {
      setStartDay((prev) => prev + visibleDaysCount);
      setIsAudioFinished(false);
    }
  };

  const handlePrevious = () => {
    if (startDay > 1) {
      setStartDay((prev) => prev - visibleDaysCount);
      setIsAudioFinished(false);
    }
  };
  const isContentLocked =
    userData?.isTrainingFreeTrialExpired &&
    userData?.isSubscriptionNeeded &&
    currentLevel?.trainingCompletionPercentage !== 100;
  //    ||
  // currentLevel?.levelName === "level3";
  const currentCurrency = useMemo(
    () =>
      countryRates[
        allowedCountries?.includes(userData?.country) ? userData?.country : "US"
      ],
    [userData]
  );
  useEffect(() => {
    if (userData?.activeLevel && !isContentLocked) {
      const existingData = JSON.parse(
        localStorage.getItem("lastProgramVisitedLevel") || "{}"
      );

      if (existingData?.level) {
        setSelectedLevel(existingData.level);
      } else {
        setSelectedLevel(userData.activeLevel);
      }
      setActiveLevelForMobile(true);
    }
  }, [userData]);
  return (
    <Box
      pb={10}
      sx={{
        padding: isMobile ? "0" : "20px 0 40px 32px"
      }}
    >
      <SubscriptionModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        isMobile={isMobile}
      />
      {((!activeLevelForMobile && isMobile) || !isMobile) && (
        <Box display="flex" flexDirection={"column"}>
          <Box
            sx={{
              ...(isMobile && {
                width: "100%",
                padding: "16px",
                display: "flex",
                alignItems: "center",
                flexDirection: "column",
                gap: "10px",
                color: "#FCFCFC"
              })
            }}
          >
            <Typography
              fontSize={isMobile ? "28px" : "30px"}
              fontWeight={600}
              ml={2}
              color={styles.color}
            >
              Programs
            </Typography>
            {/* <Typography
              textAlign={isMobile ? "center" : "left"}
              color="#6B7280"
              fontSize={isMobile ? "13px" : "16px"}
              fontWeight={isMobile ? 600 : 500}
            >
              Where you learn how to create your own diet plan
            </Typography> */}
          </Box>
          <Box
            mt={isMobile ? 0 : 3}
            gap={2}
            display={"flex"}
            flexDirection={!(isTablet || isLaptop) ? "row" : "column"}
          >
            <Box
              sx={{
                width: isLaptop
                  ? "55%"
                  : isTablet
                  ? "90%"
                  : !isMobile
                  ? { md: open ? "27%" : "24%" }
                  : "100%",
                borderRadius: "24px",
                // ...(!isMobile && {
                //   height: "calc(100vh - 40px)"
                // }),
                background: styles.levelBackgrounds,
                margin: isLaptop ? "0 auto" : 2,
                display: "flex",
                flexDirection: "column",
                padding:
                  isTablet || isMobile || isLaptop
                    ? "20px"
                    : { md: "24px 11px", lg: "24px 16px" },
                overflowY: "auto",
                ...((isTablet || isLaptop) && { height: "100%" })
              }}
              gap={4}
            >
              {allLevels?.map(
                ({
                  title,
                  trainingCompletionPercentage,
                  levelName,
                  totalTrainingDays,
                  levelActive,
                  description
                }) => (
                  <Box key={levelName} sx={{ width: "100%" }}>
                    <Button
                      fullWidth
                      onClick={async (e) => {
                        // Your existing onClick logic remains exactly the same
                        if (levelActive) handleLevelSelect(levelName);
                        if (
                          !levelActive ||
                          (!userData?.isTrainingFreeTrialExpired &&
                            userData?.isSubscriptionNeeded)
                        ) {
                          const levelId = allLevels?.find(
                            (item) => item?.levelName === levelName
                          )?.levelId;
                          if (
                            userData?.levelId?.toString() !==
                            levelId?.toString()
                          ) {
                            setSnackbarMessage(
                              `Please complete all prior trainings in order to ${
                                userData?.isAdmin ||
                                userData?.userType === "Coach" ||
                                userData?.purchased_training_by_admin?.includes(
                                  levelName
                                ) ||
                                userData?.bundleUserAmount
                                  ? "begin"
                                  : "purchase"
                              }`
                            );
                            setOpenSnackbar(true);
                            return;
                          }
                          if (
                            ["Gym", "Affiliate", "Individual"]?.includes(
                              userData?.userType
                            ) &&
                            !userData?.isAdmin &&
                            !userData?.isSubscriptionActive &&
                            levelName === "level1"
                          ) {
                            setOpenModal(true);
                            return;
                          }
                          if (userData?.userType !== "Coach") {
                            const data = await purchaseTraining(levelId);
                            window.location.href = data?.sessionUrl;
                          }
                        }
                      }}
                      sx={{
                        ...(levelActive && {
                          paddingBottom: "0 !important",
                          paddingTop: "36px !important"
                        }),
                        // Your existing sx styles remain exactly the same
                        padding: !(
                          userData?.isAdmin !== 1 &&
                          userData?.userType !== "Company" &&
                          !userData?.bundleUserAmount &&
                          !(
                            userData?.userType === "Coach" ||
                            userData?.purchased_training_by_admin?.includes(
                              levelName
                            )
                          ) &&
                          !userData?.isSuperAdmin &&
                          !userData?.isManuallyCharged &&
                          !(
                            (levelActive && !userData?.isSubscriptionNeeded) ||
                            (trainingCompletionPercentage === 100 &&
                              totalTrainingDays !== 0) ||
                            userData?.couponData?.trainingAccess?.[levelName]
                          )
                        )
                          ? isMobile
                            ? 3
                            : "45px 24px"
                          : 3,
                        alignItems: "center",
                        border:
                          selectedLevel === levelName
                            ? "1px solid #368AD0"
                            : "1px solid #D1D5DB",
                        borderRadius: "16px",
                        backgroundColor:
                          selectedLevel === levelName
                            ? "#368AD0"
                            : "transparent"
                      }}
                    >
                      <Box
                        width="100%"
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        textTransform="none"
                      >
                        <Box
                          display="flex"
                          gap="16px"
                          justifyContent="space-between"
                          alignItems="center"
                          width="100%"
                        >
                          <Box
                            textAlign={"start"}
                            gap="4px"
                            display="flex"
                            flexDirection="column"
                          >
                            {/* ALL your existing content remains exactly the same */}
                            {levelName === "level1" &&
                              !userData?.isTrainingFreeTrialExpired &&
                              userData?.isSubscriptionNeeded && (
                                <Box
                                  textAlign="start"
                                  sx={{
                                    width: "110px",
                                    background: "#368AD0",
                                    padding: "2px 6px"
                                  }}
                                >
                                  <Typography
                                    fontSize="10px"
                                    textTransform="capitalize"
                                    fontWeight="bold"
                                    fontStyle="italic"
                                    color={"#333333"}
                                  >
                                    FIRST 5 DAYS FREE
                                  </Typography>
                                </Box>
                              )}
                            <Box>
                              <Typography
                                color={"#FCFCFC"}
                                fontSize="0.95rem"
                                fontWeight={600}
                              >
                                {title} <sup>&#174;</sup>
                              </Typography>
                            </Box>
                            {/* All your existing description and buy button logic stays the same */}
                            {description?.includes("$") ? (
                              userData?.isAdmin !== 1 &&
                              userData?.userType === "Coach" &&
                              !(
                                (levelActive &&
                                  !userData?.isSubscriptionNeeded) ||
                                (trainingCompletionPercentage === 100 &&
                                  totalTrainingDays !== 0) ||
                                userData?.couponData?.trainingAccess?.[
                                  levelName
                                ]
                              ) ? (
                                <Box>
                                  <Typography
                                    width={"100%"}
                                    color={
                                      selectedLevel === levelName
                                        ? "#FCFCFC"
                                        : "#D1D5DB"
                                    }
                                    fontSize="14px"
                                  >
                                    {levelName === "level1"
                                      ? description?.replace(
                                          /unlock for (\$(\d+(\.\d{1,2})?))/i,
                                          (_, fullMatch, amountStr) => {
                                            const amount =
                                              parseFloat(amountStr);
                                            const converted =
                                              currentCurrency?.rate * amount;
                                            const formatted = Number.isInteger(
                                              converted
                                            )
                                              ? converted
                                              : converted.toFixed(2);
                                            return !levelActive &&
                                              userData?.purchased_training_by_admin?.includes(
                                                levelName
                                              )
                                              ? `to begin`
                                              : `Coach unlocks ($${amount})`;
                                          }
                                        )
                                      : description?.replace(
                                          /and then unlock for (\$(\d+(\.\d{1,2})?))/i,
                                          (_, fullMatch, amountStr) => {
                                            const amount =
                                              parseFloat(amountStr);
                                            const converted =
                                              currentCurrency?.rate * amount;
                                            const formatted = Number.isInteger(
                                              converted
                                            )
                                              ? converted
                                              : converted.toFixed(2);
                                            return !levelActive &&
                                              userData?.purchased_training_by_admin?.includes(
                                                levelName
                                              )
                                              ? `to begin`
                                              : `and then Coach unlocks ($${amount})`;
                                          }
                                        )}
                                  </Typography>
                                </Box>
                              ) : userData?.isAdmin !== 1 &&
                                userData?.userType !== "Company" &&
                                !userData?.isManuallyCharged &&
                                !userData?.isSuperAdmin &&
                                !userData?.bundleUserAmount &&
                                !(
                                  (levelActive &&
                                    !userData?.isSubscriptionNeeded) ||
                                  (trainingCompletionPercentage === 100 &&
                                    totalTrainingDays !== 0) ||
                                  userData?.couponData?.trainingAccess?.[
                                    levelName
                                  ]
                                ) ? (
                                <Box>
                                  <Typography
                                    width={"100%"}
                                    color={
                                      selectedLevel === levelName
                                        ? "#FCFCFC"
                                        : "#D1D5DB"
                                    }
                                    fontSize="14px"
                                  >
                                    {!levelActive &&
                                    userData?.purchased_training_by_admin?.includes(
                                      levelName
                                    )
                                      ? description?.replace(
                                          /and then unlock for (\$(\d+(\.\d{1,2})?))/i,
                                          "to begin"
                                        )
                                      : description?.replace(
                                          /\$\d+(\.\d{1,2})?/,
                                          (match) => {
                                            const value = parseFloat(
                                              match.slice(1)
                                            );
                                            const converted =
                                              currentCurrency?.rate * value;
                                            return `${currentCurrency?.symbol}${
                                              Number.isInteger(converted)
                                                ? converted
                                                : converted.toFixed(2)
                                            }`;
                                          }
                                        )}
                                  </Typography>
                                </Box>
                              ) : (
                                <></>
                              )
                            ) : (
                              <Box>
                                <Typography
                                  width={"80%"}
                                  color={
                                    selectedLevel === levelName
                                      ? "#FCFCFC"
                                      : "#D1D5DB"
                                  }
                                  fontSize="14px"
                                >
                                  {description}
                                </Typography>
                              </Box>
                            )}
                            {/* Your existing Buy Now button logic stays the same */}
                            {userData?.isAdmin !== 1 &&
                            userData?.userType !== "Company" &&
                            !(
                              userData?.userType === "Coach" ||
                              userData?.purchased_training_by_admin?.includes(
                                levelName
                              )
                            ) &&
                            !userData?.isSuperAdmin &&
                            !userData?.bundleUserAmount &&
                            !userData?.isManuallyCharged &&
                            !(
                              (levelActive &&
                                !userData?.isSubscriptionNeeded) ||
                              (trainingCompletionPercentage === 100 &&
                                totalTrainingDays !== 0) ||
                              userData?.couponData?.trainingAccess?.[levelName]
                            ) ? (
                              <>
                                <Button
                                  sx={{
                                    mt: 1,
                                    width: 89,
                                    height: "23px",
                                    textTransform: "none",
                                    background:
                                      selectedLevel === levelName
                                        ? "#FCFCFC"
                                        : "#D1D5DB",
                                    color:
                                      selectedLevel === levelName
                                        ? "#64AEDD"
                                        : "black",
                                    gap: "10px"
                                  }}
                                >
                                  <Typography fontSize="12px" fontWeight={500}>
                                    Buy Now
                                  </Typography>
                                  <IconButton
                                    sx={{
                                      background:
                                        selectedLevel === levelName
                                          ? "#64AEDD"
                                          : "#14142B",
                                      borderRadius: "50%",
                                      padding: "1px"
                                    }}
                                  >
                                    <ChevronRight
                                      sx={{
                                        height: 12,
                                        width: 12,
                                        color: "#FCFCFC"
                                      }}
                                    />
                                  </IconButton>
                                </Button>
                              </>
                            ) : (
                              <></>
                            )}
                          </Box>
                          <Box
                            mr={1}
                            display="flex"
                            alignItems="center"
                            gap={1}
                          >
                            {/* Your existing progress/lock icon logic */}
                            {levelActive ? (
                              <CircularProgressStatic
                                levelName={levelName}
                                selected={selectedLevel === levelName}
                                percentage={trainingCompletionPercentage}
                              />
                            ) : !levelActive &&
                              userData?.purchased_training_by_admin?.includes(
                                levelName
                              ) ? (
                              <></>
                            ) : (
                              <LockIcon />
                            )}
                          </Box>
                        </Box>
                        {levelActive && (
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleLevelExpansion(levelName);
                            }}
                            sx={{ color: "#FCFCFC", p: 0.5, mt: 2 }}
                          >
                            {expandedLevels[levelName] ? (
                              <ExpandLess />
                            ) : (
                              <ExpandMore />
                            )}
                          </IconButton>
                        )}
                      </Box>
                    </Button>

                    {/* NEW ADDITION: Training list - only show in mobile when expanded */}
                    {expandedLevels[levelName] && levelActive && (
                      <Box
                        sx={{
                          mt: 2,
                          // ml: 2,
                          // mr: 2,
                          backgroundColor: "rgba(255, 255, 255, 0.05)",
                          borderRadius: "8px"
                          // padding: "8px"
                        }}
                      >
                        {trainingWithTitleData
                          ?.find((item) => item.levelName === levelName)
                          ?.trainings?.map(
                            ({
                              trainingId,
                              day,
                              title,
                              isUnlocked,
                              isFavorite
                            }) => (
                              <Box
                                key={trainingId}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  padding: "8px 12px",
                                  borderBottom:
                                    "1px solid rgba(255, 255, 255, 0.1)",
                                  "&:last-child": {
                                    borderBottom: "none"
                                  }
                                }}
                              >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    sx={{
                                      color: "#FCFCFC",
                                      fontSize: "14px",
                                      fontWeight: 500,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      fontStyle: "italic"
                                    }}
                                  >
                                    <strong style={{ color: "#64AEDD" }}>
                                      {" "}
                                      {day}
                                    </strong>{" "}
                                    {title}
                                  </Typography>
                                </Box>
                                <IconButton
                                  sx={{
                                    padding: "0 !important",
                                    cursor: !isUnlocked
                                      ? "not-allowed"
                                      : "pointer"
                                  }}
                                  onClick={async (e) => {
                                    if (!isUnlocked) return;
                                    e.stopPropagation();
                                    dispatch(
                                      updateTrainingById({
                                        id: trainingId,
                                        updates: {
                                          isFavorite: !isFavorite
                                        }
                                      })
                                    );

                                    dispatch(
                                      updateTrainingProgressById({
                                        id: trainingId,
                                        updates: {
                                          isFavorite: !isFavorite
                                        }
                                      })
                                    );

                                    await updateTrainingProgress({
                                      trainingId: trainingId,
                                      isFavorite: !isFavorite
                                    });
                                  }}
                                >
                                  {isFavorite ? (
                                    <Bookmark
                                      sx={{
                                        height: "18px",
                                        width: "18px",
                                        fill: "#03B55C"
                                      }}
                                    />
                                  ) : (
                                    <BookmarkBorder
                                      sx={{
                                        height: "18px",
                                        width: "18px",
                                        fill: styles.color
                                      }}
                                    />
                                  )}
                                </IconButton>
                                <Box
                                  sx={{
                                    ml: 1,
                                    display: "flex",
                                    alignItems: "center"
                                  }}
                                  onClick={() => {
                                    if (!isUnlocked) return;
                                    const dayNumber = +day?.split(" ")[1];
                                    setActiveDay(dayNumber);
                                    setTocDay(dayNumber);
                                    localStorage.setItem(
                                      "lastProgramVisitedLevel",
                                      JSON.stringify({
                                        level: levelName,
                                        activeDay: dayNumber
                                      })
                                    );
                                    const pageIndex = Math.floor(
                                      (dayNumber - 1) / visibleDaysCount
                                    );
                                    // Calculate startDay for that page
                                    const calculatedStartDay =
                                      pageIndex * visibleDaysCount + 1;
                                    setStartDay(calculatedStartDay);
                                    setSelectedLevel(levelName);
                                    setActiveLevelForMobile(true);
                                    window.scrollTo(0, 0);
                                    const scrollableElement =
                                      document.getElementById("dashboard-main");
                                    scrollableElement.scrollTo(0, 0);
                                  }}
                                >
                                  {isUnlocked ? (
                                    <PlayCircleFilled
                                      sx={{
                                        color: "#64AEDD",
                                        fontSize: 20,
                                        ":hover": {
                                          cursor: "pointer"
                                        }
                                      }}
                                    />
                                  ) : (
                                    <Lock
                                      sx={{
                                        color: "#9E9E9E",
                                        fontSize: 20,
                                        ":hover": {
                                          cursor: "not-allowed"
                                        }
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            )
                          )}
                      </Box>
                    )}
                  </Box>
                )
              )}
            </Box>
            <Snackbar
              open={openSnackbar}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              autoHideDuration={4000}
              onClose={() => setOpenSnackbar(false)}
            >
              <Alert
                onClose={() => setOpenSnackbar(false)}
                severity={
                  snackbarMessage.includes("Success") ? "success" : "error"
                }
                variant="filled"
                sx={{ width: "100%" }}
              >
                {snackbarMessage}
              </Alert>
            </Snackbar>
            {!isMobile && selectedLevel && (
              <Box
                display="flex"
                sx={{
                  width: isLaptop
                    ? "100%"
                    : !isMobile
                    ? { md: open ? "68%" : "73%" }
                    : "100%",
                  padding: "0 25px"
                  // ...((!isTablet || !isMobile) && {
                  //   maxHeight: "calc(100vh - 40px)"
                  // })
                }}
                flexDirection="column"
                gap={3}
                ml={isMobile ? 0 : 2}
              >
                {isContentLocked ? (
                  <Box
                    sx={{
                      background:
                        "radial-gradient(circle at 50% 50%, #368AD0, #2D5F87, #1A3A5C)",
                      width: "100%",
                      margin: "24px 16px",
                      height: "100%",
                      zIndex: 1,
                      borderRadius: "8px"
                    }}
                  >
                    <Box
                      display="flex"
                      borderRadius="8px"
                      justifyContent="center"
                      alignItems="center"
                      mr={1}
                      sx={{
                        width: "100%",
                        height: "100%",
                        backdropFilter: "blur(8px)" // blur effect
                      }}
                    >
                      <LockIcon
                        style={{ color: "#FCFCFC", height: 70, width: 70 }}
                      />
                    </Box>
                  </Box>
                ) : (
                  <>
                    <DaySelector
                      trainingWithTitleData={trainingWithTitleData?.find(
                        (level) => level.levelName === selectedLevel
                      )}
                      enabledDays={
                        currentLevel?.trainingCompletionPercentage === 100 &&
                        currentLevel?.totalTrainingDays !== 0
                          ? Array.from(
                              { length: currentLevel?.completedTrainingDays },
                              (_, i) => i + 1
                            )
                          : !userData?.isSubscriptionNeeded
                          ? Array.from(
                              {
                                length:
                                  dayjs()
                                    .startOf("day")
                                    .diff(
                                      dayjs(
                                        dayjs(
                                          userData?.[
                                            `${currentLevel?.levelName}_start_date`
                                          ]
                                        )
                                          .utc()
                                          .format("YYYY-MM-DD")
                                      ).startOf("day"),
                                      "day"
                                    ) + 1
                              },
                              (_, i) => i + 1
                            )
                          : !userData?.isTrainingFreeTrialExpired
                          ? Array.from(
                              {
                                length: Math.min(
                                  5,
                                  dayjs()
                                    .startOf("day")
                                    .diff(
                                      dayjs(
                                        dayjs(
                                          userData?.[
                                            `${currentLevel?.levelName}_start_date`
                                          ]
                                        )
                                          .utc()
                                          .format("YYYY-MM-DD")
                                      ).startOf("day"),
                                      "day"
                                    ) + 1
                                )
                              },
                              (_, i) => i + 1
                            )
                          : []
                      }
                      allCompletedData={allCompletedData}
                      activeDay={activeDay}
                      setActiveDay={setActiveDay}
                      startDay={startDay}
                      visibleDays={visibleDays}
                      visibleDaysCount={visibleDaysCount}
                      days={days}
                      setStartDay={setStartDay}
                      trainingData={trainingData}
                      handleDayClick={handleDayClick}
                      handleNext={handleNext}
                      handlePrevious={handlePrevious}
                    />
                    <Box
                      sx={{
                        ...((!isTablet || !isMobile) && {
                          overflowY: "auto",
                          ...(!Object.values(expandedLevels)?.filter(Boolean)
                            ?.length && {
                            maxHeight: "calc(100vh - 120px)"
                          }),
                          position: "relative"
                        })
                      }}
                    >
                      <RenderHtml
                        key={`render-html-${activeDay}`}
                        isMobile={isMobile}
                        selectedLevel={selectedLevel}
                        currentLevel={currentLevel}
                        activeLevelForMobile={activeLevelForMobile}
                        setIsAudioFinished={setIsAudioFinished}
                        isAudioFinished={isAudioFinished}
                        styles={styles}
                        activeDay={activeDay}
                        activeData={currentActiveData}
                      />
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}
      {activeLevelForMobile && isMobile && (
        <Box
          display="flex"
          sx={{
            width: "100%",
            padding: "10px 25px"
          }}
          flexDirection="column"
          gap={3}
        >
          <Box
            display="flex"
            alignItems="center"
            gap={isInMobile ? "33%" : "16%"}
          >
            <IconButton
              onClick={() => {
                setActiveLevelForMobile(false);
                setTocDay(0);
              }}
            >
              <ChevronLeft
                sx={{
                  width: 30,
                  height: 30,
                  color: styles.color
                }}
                width={30}
                height={30}
              />
            </IconButton>
            <Typography fontWeight={500} fontSize={"24px"}>
              {" "}
              {currentLevel?.title}{" "}
            </Typography>
          </Box>
          {isContentLocked ? (
            <Box
              sx={{
                background:
                  "radial-gradient(circle at 50% 50%, #368AD0, #2D5F87, #1A3A5C)",
                width: "100%",
                margin: "24px 16px",
                height: "400px",
                zIndex: 1,
                borderRadius: "8px"
              }}
            >
              <Box
                display="flex"
                borderRadius="8px"
                justifyContent="center"
                alignItems="center"
                mr={1}
                sx={{
                  width: "100%",
                  height: "100%",
                  backdropFilter: "blur(8px)" // blur effect
                }}
              >
                <LockIcon style={{ color: "#FCFCFC", height: 70, width: 70 }} />
              </Box>
            </Box>
          ) : (
            <>
              <DaySelector
                trainingWithTitleData={trainingWithTitleData?.find(
                  (level) => level.levelName === selectedLevel
                )}
                enabledDays={
                  currentLevel?.trainingCompletionPercentage === 100 &&
                  currentLevel?.totalTrainingDays !== 0
                    ? Array.from(
                        { length: currentLevel?.completedTrainingDays },
                        (_, i) => i + 1
                      )
                    : !userData?.isSubscriptionNeeded
                    ? Array.from(
                        {
                          length:
                            dayjs()
                              .startOf("day")
                              .diff(
                                dayjs(
                                  dayjs(
                                    userData?.[
                                      `${currentLevel?.levelName}_start_date`
                                    ]
                                  )
                                    .utc()
                                    .format("YYYY-MM-DD")
                                ).startOf("day"),
                                "day"
                              ) + 1
                        },
                        (_, i) => i + 1
                      )
                    : !userData?.isTrainingFreeTrialExpired
                    ? Array.from(
                        {
                          length: Math.min(
                            5,
                            dayjs()
                              .startOf("day")
                              .diff(
                                dayjs(
                                  dayjs(
                                    userData?.[
                                      `${currentLevel?.levelName}_start_date`
                                    ]
                                  )
                                    .utc()
                                    .format("YYYY-MM-DD")
                                ).startOf("day"),
                                "day"
                              ) + 1
                          )
                        },
                        (_, i) => i + 1
                      )
                    : []
                }
                allCompletedData={allCompletedData}
                activeDay={activeDay}
                setActiveDay={setActiveDay}
                startDay={startDay}
                visibleDays={visibleDays}
                visibleDaysCount={visibleDaysCount}
                days={days}
                setStartDay={setStartDay}
                trainingData={trainingData}
                handleDayClick={handleDayClick}
                handleNext={handleNext}
                handlePrevious={handlePrevious}
              />
              <Box>
                <RenderHtml
                  key={`render-html-${activeDay}`}
                  isMobile={isMobile}
                  selectedLevel={selectedLevel}
                  currentLevel={currentLevel}
                  activeLevelForMobile={activeLevelForMobile}
                  setIsAudioFinished={setIsAudioFinished}
                  isAudioFinished={isAudioFinished}
                  styles={styles}
                  activeDay={activeDay}
                  activeData={currentActiveData}
                />
              </Box>{" "}
            </>
          )}
        </Box>
      )}
      {(isMobile ? selectedLevel && activeLevelForMobile : selectedLevel) &&
        !isContentLocked &&
        currentLevel?.link && (
          <Box
            sx={{
              position: "fixed",
              bottom: { xs: 95, sm: 20, md: 20 },
              right: 16,
              zIndex: 1000
            }}
          >
            <IconButton
              variant="contained"
              sx={{
                display: "flex",
                backgroundColor: "#FF4000",
                borderRadius: "24px",
                color: "#FCFCFC",
                padding: "7px 12px",
                fontSize: "16px",
                textTransform: "none",
                gap: "10px",
                fontWeight: "bold",
                "&:hover": {
                  backgroundColor: "#FF7043"
                }
              }}
              onClick={() => {
                const url = currentLevel?.link.startsWith("http")
                  ? currentLevel?.link
                  : `http://${currentLevel?.link}`;
                window.open(url, "_blank");
              }}
            >
              <QuestionAnswer />
              {/* <Typography
                sx={{
                  background: "#FCFCFC",
                  color: "#506DF0",
                  borderRadius: "50%",
                  padding: "7px 12px"
                }}
              >
                C
              </Typography>
              <Typography color="#FCFCFC">Join Us</Typography> */}
            </IconButton>
          </Box>
        )}
    </Box>
  );
};

export default Programs;
